<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed } from "vue";
import { Configuration, ThreadsApi, type ThreadDetailsResponse, type ReservationDetails, ReservationsApi, GuestsApi } from "@/api/javaapi";
import { useAuthStore } from "@/stores/auth.store";
import { useThreadStore } from "@/stores/thread.store";
import { RouterLink } from "vue-router";
import {Accordion, AccordionPanel, AccordionHeader, AccordionContent, InputText, Textarea, Button, FloatLabel} from "primevue";
import { useToast } from "primevue/usetoast";

const props = defineProps<{ threadId: number }>();
const authStore = useAuthStore();
const threadStore = useThreadStore();
const toast = useToast();

const apiConfig = new Configuration({ accessToken: authStore.token ?? undefined });
const threadsApi = new ThreadsApi(apiConfig);
const guestApi = new GuestsApi(apiConfig);
const reservationsApi = new ReservationsApi(apiConfig);
const isLoading = ref(true);
const threadDetails = ref<ThreadDetailsResponse | null>(null);
const newNote = ref<Record<number, string>>({});
const today = new Date().toISOString().split('T')[0];

const airbnbLogo = new URL("@/assets/images/airbnb.png", import.meta.url).href;
const bdcLogo = new URL("@/assets/images/bdc.png", import.meta.url).href;
const vrboLogo = new URL("@/assets/images/vrbo.png", import.meta.url).href;

const channelIcon = computed(() => {
  switch (thread.value?.channel) {
    case "AIRBNB":
      return airbnbLogo;
    case "BDC":
      return bdcLogo;
    case "VRBO":
      return vrboLogo;
    case "EMAIL":
      return "pi pi-envelope";
    case "TEXT":
      return "pi pi-mobile";
    default:
      return null;
  }
});

const activeReservation = computed(() => {
  const reservations = threadDetails.value?.reservations;
  if (!reservations || !reservations.length) return -1;

  const scored = reservations.map(res => ({
    res,
    score: 
      res.status === 'CHECKED_IN' ? 4 : // CHECKED_IN
      res.arriveDate <= today && res.departDate >= today ? 3 : // Current
      res.status === 'RESERVED' && res.departDate <= today ? 2 : // RESERVED but incomplete.
      res.status === 'RESERVED' ? 1 : // Earliest RESERVED
      0 // Fallback
  }));

  return scored.sort((a, b) => b.score - a.score)[0]?.res.id ?? -1;
});

const thread = computed(() => threadStore.getOpenThread(props.threadId));

declare global {
  interface Window {
    GuestSelected?: (gstWin: Window) => void;
  }
}

async function loadThreadDetails(threadId: number) {
  isLoading.value = true;
  if (!threadId) {
    console.debug("Skipping thread details fetch: invalid threadId", { threadId });
    isLoading.value = false;
    return;
  }

  // Wait for thread to be fully loaded
  const waitForThread = () => {
    return new Promise<void>((resolve, reject) => {
      if (threadStore.getOpenThread(threadId)) {
        resolve();
        return;
      }
      const interval = setInterval(() => {
        if (threadStore.getOpenThread(threadId)) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
      setTimeout(() => {
        clearInterval(interval);
        reject(new Error("Thread failed to load"));
      }, 5000); // Timeout after 5s
    });
  };

  try {
    await waitForThread();
    console.debug("Thread loaded:", thread.value);

    console.debug("Fetching thread details for ID:", threadId);
    const response = await threadsApi.getThreadDetails(threadId);
    threadDetails.value = response.data;
    console.debug("Thread details loaded:", threadDetails.value);
  } catch (error) {
    console.error("Failed to load thread or details:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to load thread details",
      life: 3000,
    });
  } finally {
    isLoading.value = false;
  }
}

function GuestSelected(gstWin: Window) {
  gstWin.close();
  loadThreadDetails(props.threadId);
}

onMounted(() => {
  loadThreadDetails(props.threadId);
  window.GuestSelected = GuestSelected;
});
onUnmounted(() => {
  delete window.GuestSelected;
});

watch(
  () => props.threadId,
  (newId) => {
    console.debug("ThreadId changed:", newId);
    threadDetails.value = null;
    loadThreadDetails(newId);
  }
);

async function saveGuestNotes(notes: string) {
  if (threadDetails.value?.guest) {
    try {
      await guestApi.updateGuestNotes(threadDetails.value.guest.id, notes);
      threadDetails.value.guest.notes = notes;
      toast.add({ severity: "success", summary: "Success", detail: "Guest notes updated", life: 3000 });
    } catch (error) {
      console.error("Failed to save guest notes:", error);
      toast.add({ severity: "error", summary: "Error", detail: "Failed to save guest notes", life: 3000 });
    }
  }
}

async function saveReservationNotes(reservationId: number, notes: string) {
  try {
    await reservationsApi.updateReservationNotes(reservationId, notes);
    const res = threadDetails.value?.reservations.find((r) => r.id === reservationId);
    if (res) res.notes = notes;
    toast.add({ severity: "success", summary: "Success", detail: "Reservation notes updated", life: 3000 });
  } catch (error) {
    console.error("Failed to save reservation notes:", error);
    toast.add({ severity: "error", summary: "Error", detail: "Failed to save reservation notes", life: 3000 });
  }
}

async function addReservationNote(reservationId: number) {
  const note = newNote.value[reservationId]?.trim();
  if (!note) {
    toast.add({ severity: "warn", summary: "Empty Note", detail: "Please enter a note", life: 3000 });
    return;
  }
  try {
    const response = await reservationsApi.addReservationNote(reservationId, note);
    const res = threadDetails.value?.reservations.find((r) => r.id === reservationId);
    if (res) res.notes = response.data;
    newNote.value[reservationId] = '';
    toast.add({ severity: "success", summary: "Success", detail: "Note added", life: 3000 });
  } catch (error) {
    console.error("Failed to add note:", error);
    toast.add({ severity: "error", summary: "Error", detail: "Failed to add note", life: 3000 });
  }
}

async function saveArrivalTime(reservationId: number, arrivalTime: string) {
  try {
    await reservationsApi.updateArrivalTime(reservationId, arrivalTime);
    const res = threadDetails.value?.reservations.find((r) => r.id === reservationId);
    if (res) res.arriveTime = arrivalTime;
    toast.add({ severity: "success", summary: "Success", detail: "Arrival time updated", life: 3000 });
  } catch (error) {
    console.error("Failed to save arrival time:", error);
    toast.add({ severity: "error", summary: "Error", detail: "Failed to save arrival time", life: 3000 });
  }
}

async function checkIn(reservationId: number) {
  try {
    await reservationsApi.checkInReservation(reservationId);
    await loadThreadDetails(props.threadId);
    toast.add({ severity: "success", summary: "Success", detail: "Checked in", life: 3000 });
  } catch (error) {
    console.error("Failed to check in:", error);
    toast.add({ severity: "error", summary: "Error", detail: "Failed to check in", life: 3000 });
  }
}

async function checkOut(reservationId: number) {
  try {
    await reservationsApi.checkOutReservation(reservationId);
    await loadThreadDetails(props.threadId);
    toast.add({ severity: "success", summary: "Success", detail: "Checked out", life: 3000 });
  } catch (error) {
    console.error("Failed to check out:", error);
    toast.add({ severity: "error", summary: "Error", detail: "Failed to check out", life: 3000 });
  }
}

function isCurrentOrFuture(reservation: ReservationDetails): boolean {
  return reservation.status === 'RESERVED' || reservation.status === 'CHECKED_IN' || reservation.departDate >= today;
}

function openUnitPopup(unitId: number | null) {
  if (unitId) {
    const url = `/RM4/UnitDetails.php?id=${unitId}`;
    window.open(url, "newWindow", "width=640,height=700,scrollbars=yes,resizable=yes");
  }
}

function openGuestPopup(guestId: number | null, rsvId: number | null) {
  if (guestId) {
    const url = rsvId ? `/RM4/GuestSelect.php?id=${guestId}&rsv=${rsvId}` : `/RM4/GuestSelect.php?id=${guestId}`;
    window.open(url, "newWindow", "width=640,height=700,scrollbars=yes,resizable=yes");
  }
}

function formatDate(date: string | null): string {
  if (!date) return "N/A";

  const [year, month, day] = date.split("-").map(Number);
  const dateObj = new Date(year, month - 1, day);
  
  return dateObj.toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatStatus(status: string | undefined): string {
  switch (status) {
    case 'RESERVED': return 'Reserved';
    case 'CHECKED_IN': return 'Checked In';
    case 'CHECKED_OUT': return 'Checked Out';
    case 'POSTED': return 'Posted';
    case 'CANCELLED': return 'Cancelled';
    default: return "";
  }
}

function getStatusIcon(status: string | undefined): string {
  switch (status) {
    case 'RESERVED': return 'calendar-day';
    case 'CHECKED_IN': return 'calendar-circle-user';
    case 'CHECKED_OUT': return 'calendar-check';
    case 'POSTED': return 'calendar-check';
    case 'CANCELLED': return 'calendar-xmark';
    default: return "";
  }
}
</script>

<template>
  <div class="p-4">
    <div v-if="isLoading">
      <p>Loading thread details...</p>
    </div>
    <div v-else-if="!threadDetails">
      <p>No thread details found.</p>
    </div>
    <div v-else>
      <div v-if="channelIcon" class="absolute top-0 right-4 p-2">
        <img v-if="typeof channelIcon === 'string' && channelIcon.startsWith('http')" :src="channelIcon" class="w-7 h-7 object-contain rounded-full" />
        <i v-else="channelIcon" :class="channelIcon" class="text-gray-700 dark:text-gray-300 text-xl" />
      </div>
      <p v-if="thread?.unitName && thread.unit && threadDetails.reservations.length === 0">
        <strong>Unit: </strong>
        <Button variant="link" @click="openUnitPopup(thread.unit)" class="p-0">{{ thread.unitName }}</Button>
      </p>
      <p v-if="thread?.arrive && threadDetails.reservations.length === 0">
        <strong>Arrival:</strong> {{ formatDate(thread.arrive) }}
      </p>
      <p v-if="thread?.depart && threadDetails.reservations.length === 0">
        <strong>Departure:</strong> {{ formatDate(thread.depart) }}
      </p>

      <div v-if="threadDetails.guest">
        <h2 class="text-lg font-semibold mb-2">Guest Information</h2>
        <p>
          <strong>Name: </strong>
          <Button variant="link" v-if="authStore.hasPermission('Guests', 'View')" class="p-0"
            @click="openGuestPopup(threadDetails.guest.id, thread?.reservation || null)">
            {{ threadDetails.guest.name }}
          </Button>
          <span v-else>{{ threadDetails.guest.name }}</span>
        </p>
        <p v-if="threadDetails.guest.address"><strong>Address:</strong> {{ threadDetails.guest.address }}</p>
        <p v-if="threadDetails.guest.phoneNumbers"><strong>Phone:</strong> {{ threadDetails.guest.phoneNumbers }}</p>
        <p v-if="threadDetails.guest.email"><strong>Email:</strong> {{ threadDetails.guest.email }}</p>
        <FloatLabel variant="on" class="mt-2">
          <Textarea v-model="threadDetails.guest.notes" @change="saveGuestNotes(threadDetails.guest.notes)" class="w-full h-[120px]" size="small"/>
          <label>Notes</label>
        </FloatLabel>
      </div>

      <h2 class="text-lg font-semibold mt-4 mb-2">Reservations</h2>
      <Accordion v-if="threadDetails.reservations.length > 0" :value="activeReservation">
        <AccordionPanel v-for="res in threadDetails.reservations" :value="res.id">
          <AccordionHeader>
            <span>
              <font-awesome-icon :icon="getStatusIcon(res.status)" class="mr-2" :title="formatStatus(res.status)"/>
            <Button variant="link" v-if="authStore.hasPermission('Reservations', 'Change')" class="p-0"><RouterLink :to="`/rm4/ReserveEdit.php?confnum=${res.id}`">{{ res.id }}</RouterLink></Button>
            <span v-else>{{ res.id }}</span>
            - {{formatDate(res.arriveDate)}}
            </span>
          </AccordionHeader>
          <AccordionContent>
          <p>
            <strong>Unit:&nbsp;</strong>
            <Button variant="link" @click="openUnitPopup(res.unitId)" class="p-0">{{ res.unitName }}</Button>
          </p>
          <p><strong>Arrival: </strong> {{ formatDate(res.arriveDate) }}</p>
          <p><strong>Departure: </strong> {{ formatDate(res.departDate) }}</p>
          <p>
            <strong>Status: </strong>
            {{ formatStatus(res.status) }}
            <span v-if="res.status === 'RESERVED' && authStore.hasPermission('Reservations', 'Change') && res.arriveDate <= today">
            <span v-if="!res.noCheckInOutReason">
              <Button label="Check In" @click="checkIn(res.id)" size="small"/>
            </span>
            <span v-else> - {{ res.noCheckInOutReason }}</span>
          </span>
          <span v-else-if="res.status === 'CHECKED_IN' && authStore.hasPermission('Reservations', 'Change')">
            <span v-if="!res.noCheckInOutReason">
              <Button label="Check Out" @click="checkOut(res.id)" size="small"/>
            </span>
            <span v-else> - {{ res.noCheckInOutReason }}</span>
          </span>

          </p>
          <p v-if="isCurrentOrFuture(res)"><strong>Key Code:</strong> {{ res.keyCode }}</p>
          <div class="flex items-center" v-if="isCurrentOrFuture(res)">
            <strong>Arrival&nbsp;Time:&nbsp;</strong>
            <InputText 
              v-model="res.arriveTime" 
              @change="saveArrivalTime(res.id, res.arriveTime)" 
              size="small"
              class="w-full" 
              maxlength="10" 
            />
          </div>
          <FloatLabel variant="on" class="mt-2">
            <label>Notes</label>
            <Textarea 
              v-model="res.notes" 
              :disabled="!authStore.hasPermission('Reservations', 'Override')" 
              @change="saveReservationNotes(res.id, res.notes)" 
              class="w-full h-[120px]"
              size="small"
            />
          </FloatLabel>
          <p class="flex">
            <Textarea v-model="newNote[res.id]" class="w-full" autoResize placeholder="Add Note" size="small" rows="1" @keydown.enter.exact.prevent="addReservationNote(res.id)"/>
            <Button label="Add" @click="addReservationNote(res.id)" class="ml-2" size="small"/>
          </p>
        </AccordionContent>
        </AccordionPanel>
      </Accordion>
      <p v-else>No reservations found.</p>

      <div v-if="threadDetails.threads?.length > 0">
        <h2 class="text-lg font-semibold mt-4 mb-2">Other Threads</h2>
        <ul class="list-disc pl-5">
          <li v-for="t in threadDetails.threads" :key="t.id">
            <Button variant="link" class="p-0 text-left"><RouterLink :to="`/inbox/${t.id}`">
              {{ t.channel === "TEXT" ? `${t.externalId}` : t.channel }} ({{ t.messageCount }} messages)
            </RouterLink></Button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>